import styled from '@emotion/styled'
import { useAtomValue } from 'jotai'

import { DefaultTrigger } from '../DefaultTrigger'
import { Flex } from '../Flex'
import { ProfileMenu } from '../ProfileMenu'
import { Headline, Item } from '../Typography'

import { AvatarIcon, LogoutIcon } from '@/assets/icons'
import { colors, ItemConfig } from '@/constants'
import { SIDEBAR_CONFIG } from '@/constants/sideBarConfig'
import { useLogout } from '@/features/Auth/application/useLogout'
import { sidebarLocationPathAtom } from '@/store/sidebar'
import { store } from '@/store/store'
import { userAtom } from '@/features/Auth/infrastructure/user'
import { findChild, getItemUrl } from '@/utils/subMenu'

const SidebarWrapper = styled(Flex)`
  flex-grow: 1;
  flex-direction: column;
`

const TopBlock = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  gap: 15px;
`

const IconWithText = styled(Flex)<{ isActive?: boolean; disabled?: boolean }>`
  gap: 1rem;
  width: 100%;
  height: 3.375rem;
  align-items: center;
  border-radius: 1rem;
  transition-property: color, opacity, background-color;
  transition-duration: 300ms;
  color: ${colors.light2};
  position: relative;

  ${({ isActive }) => isActive && `color: ${colors.white};`}

  ${({ disabled }) =>
    disabled
      ? `opacity: 0.5;`
      : `
        cursor: pointer;
        
        &:hover {
          background-color: ${colors.darkBlue3};
        }
        
        &:active {
          background-color: ${colors.darkBlue2};
        }
      `}
  
  &[role="button"] {
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${colors.blue};
    }
  }
`

const IconWrapper = styled(Flex)`
  padding: 15px;
  background-color: ${colors.darkBlue2};
  border-radius: 1.25rem;
`

const Footer = styled(Flex)`
  height: 4rem;
  border-top: 1px solid ${colors.light1};
  align-items: center;
  justify-content: space-between;
  align-items: center;
`

const LogoutWrapper = styled(Flex)`
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background-color: ${colors.darkBlue3};
  }

  /* Ensure focus is visible in all browsers */
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue};
  }
`

export default function MobileSidebarContent({
  config = SIDEBAR_CONFIG,
  onClick,
  isSubMenu,
}: {
  config?: ItemConfig[]
  isSubMenu?: boolean
  onClick: () => void
}) {
  const logout = useLogout()
  const user = useAtomValue(userAtom)
  const location = useAtomValue(sidebarLocationPathAtom)
  const current = findChild(location, config)
  const currentConfig = current?.submenu ? current.submenu : config

  return (
    <SidebarWrapper>
      <TopBlock>
        {currentConfig.map((item) => (
          <IconWithText
            isActive={location === getItemUrl(item)}
            key={`mobile-sidebar-item-${item.label}`}
            onClick={() => {
              store.set(sidebarLocationPathAtom, getItemUrl(item))
              if (!item.submenu) onClick()
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                store.set(sidebarLocationPathAtom, getItemUrl(item))
                if (!item.submenu) onClick()
              }
            }}
            aria-label={`Navigate to ${item.label}`}
          >
            {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
            <Headline as={Item}>{item.label}</Headline>
          </IconWithText>
        ))}
      </TopBlock>
      {isSubMenu ? (
        <Footer>
          <LogoutWrapper
            onClick={logout}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                logout()
              }
            }}
          >
            <LogoutIcon />
            <Item>Log out</Item>
          </LogoutWrapper>
        </Footer>
      ) : (
        true && (
          <ProfileMenu
            align="end"
            side="top"
            trigger={(trigger) => (
              <Footer>
                <Flex
                  gap="0.625rem"
                  alignItems="center"
                  onClick={() => trigger.setOpen(!trigger.open)}
                >
                  <AvatarIcon width="2.25rem" height="2.25rem" />
                  <Item color="white">{'user@example.com'}</Item>
                </Flex>
                <DefaultTrigger
                  containerId={trigger.containerId}
                  {...trigger}
                />
              </Footer>
            )}
          />
        )
      )}
    </SidebarWrapper>
  )
}
